/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AgentResponse,
  AgentResponseVerificationState,
} from "../../types/AgentResponse";
import { WebSocketChannel } from "../../types/WebSocketChannel";
import type {
  WebSocketChatMessage,
  WebSocketFeedbackMessage,
  WebSocketChatTemplatesMessage,
} from "../../types/WebSocketMessage";
import { useWebSocketListener } from "./useWebSocketListener";
import { generateUuid, isString } from "@huub-web/utils";
import {
  nextFrame,
  type PubSubMap,
  usePubSub,
  useDebug,
  type NavigationContext,
  type ChatConversation,
} from "@huub-web/ui";

/**
 * Hook for listening for and managing chat messages.
 *
 * The `TTemplates` parameter types the `templates` field on agent responses
 * and template-bearing WebSocket messages. Pass your own template type
 * (e.g. `useChatMessages<TemplateCollection>({ ... })`) to get the strict
 * shape at call sites; defaults to `unknown[]`.
 *
 * @deprecated This hook is should not be used other then in AppController. Use useAppController if needed elsewhere.
 * @todo Gets the job done, but needs refactoring.
 */
export const useChatMessages = <
  TTemplates extends unknown[] = unknown[],
>(config: {
  onMessage?: (message: AgentResponse<TTemplates>) => void;
  onDuplicateMessage?: (id: string, body?: string) => void;
}) => {
  const pubSub = usePubSub();
  const { log } = useDebug();

  const [hasFirstAgentResponse, setHasFirstAgentResponse] = useState(false);
  const [agentResponses, setAgentResponses] = useState<
    Record<string, AgentResponse<TTemplates>>
  >({});
  const [isStreaming, setIsStreaming] = useState(false);

  /**
   * Tracks message IDs that have received their final (streaming=false)
   * response. Late-arriving streaming chunks for these IDs are dropped
   * to prevent the last chunk from being appended to already-complete text.
   */
  const completedMessageIDsRef = useRef<Set<string>>(new Set());

  type AgentResponsesMapEntry = {
    context: NavigationContext;
    interaction: boolean;
    interactionLabel: string;
    verificationState: AgentResponseVerificationState;
  };

  /**
   * Ref-based map of agent response metadata keyed by message ID.
   * A ref is used instead of state because the data is consumed
   * synchronously within the same tick it's written (e.g. inside
   * WebSocket handlers and pub/sub callbacks). React state updates
   * are batched/async and would return stale data in those paths.
   */
  const agentResponsesMapRef = useRef<Record<string, AgentResponsesMapEntry>>(
    {},
  );

  /**
   * @deprecated
   */
  const templates = useRef<TTemplates | null>(null);

  // Memoized list of agent response IDs
  const agentResponseIDs = useMemo(
    () => Object.keys(agentResponses),
    [agentResponses],
  );

  // Agent response messages appending
  // ---------------------------------

  const addAgentResponse = (message: AgentResponse<TTemplates>) => {
    addOrAppendAgentResponse(message);

    setConversation((prevThread) => ({
      ...prevThread,
      entries: [
        ...prevThread.entries,
        {
          id: message.id,
          author: "agent",
          content: message.body ?? "",
          created: new Date().toISOString(),
          context: message.context,
          interaction: message.interaction,
          interactionLabel: message.interaction_label,
          verificationState: message.verification_state ?? "default",
        },
      ],
    }));
  };

  const addOrAppendAgentResponse = (message: AgentResponse<TTemplates>) => {
    log(
      "cyan",
      "useChatMessages",
      "Adding or appending agent response",
      message,
    );

    if (!message.id) {
      console.warn("Chat message without ID", message);
      return;
    }

    if (ignoreMessageIDs.current.includes(message.id)) {
      log("orange", "useChatMessages", "Ignoring message", message.id);
      return;
    }

    setAgentResponses((prevMessages) => {
      const prevMessage = prevMessages[message.id];

      return {
        ...prevMessages,
        [message.id]: { ...prevMessage, ...message },
      };
    });
  };

  const addOrAppendStreamingAgentResponse = (
    message: AgentResponse<TTemplates>,
  ) => {
    if (completedMessageIDsRef.current.has(message.id)) {
      log(
        "orange",
        "useChatMessages",
        "Dropping late streaming chunk for completed message",
        message.id,
      );
      return;
    }

    setAgentResponses((prevMessages) => {
      const prevMessage = prevMessages[message.id];

      if (ignoreMessageIDs.current.includes(message.id)) {
        log("orange", "useChatMessages", "Ignoring message", message.id);
        return prevMessages;
      }

      if (!prevMessage) {
        return {
          ...prevMessages,
          [message.id]: message,
        };
      }

      return {
        ...prevMessages,
        [message.id]: {
          ...prevMessage,
          body: (prevMessage.body ?? "") + (message.body ?? ""),
        },
      };
    });
  };

  // Agent response messages
  // ------------------------

  useWebSocketListener<WebSocketChatMessage>(
    WebSocketChannel.CHAT,
    (received) => {
      // console.log("[useChatMessages] WebSocketChatMessage received", received);

      const ID =
        received.data.message_id ??
        (received.data as any).id ?? // Fallback for old messages
        generateUuid();

      const response: AgentResponse<TTemplates> = {
        id: ID,
        body: received?.data?.message ?? "",
      };

      if (
        received?.data?.context ||
        received?.data?.interaction ||
        received?.data?.interaction_label
      ) {
        log(
          "cyan",
          "useChatMessages",
          "Setting message context",
          received.data.context,
        );

        if (received.data.context) {
          response.context = received.data.context;
        }
        if (received.data.interaction) {
          response.interaction = received.data.interaction;
        }
        if (received.data.interaction_label) {
          response.interaction_label = received.data.interaction_label;
        }
        if (received.data.verification_state) {
          response.verification_state = received.data.verification_state;
        }

        const entry = {
          context: received.data.context ?? [],
          interaction: received.data.interaction ?? false,
          interactionLabel: received.data.interaction_label ?? "",
          verificationState: received.data.verification_state ?? "default",
        };

        agentResponsesMapRef.current = {
          ...agentResponsesMapRef.current,
          [ID]: entry,
        };
      }

      setHasFirstAgentResponse(true);

      // If streaming, append to last message
      if (received.data.streaming) {
        setIsStreaming(true);

        addOrAppendStreamingAgentResponse(response);
        config.onMessage?.(response);
      } else {
        // Not streaming / end of message
        completedMessageIDsRef.current.add(ID);

        response.templates = [
          ...(templates.current ?? []),
        ] as unknown as TTemplates;
        templates.current = null;

        setIsStreaming(false);

        addOrAppendAgentResponse(response);
        config.onMessage?.(response);

        // System-action messages (interaction=true) arrive without a
        // preceding user prompt, so nothing pushes the scroll down.
        // Trigger a scroll so old messages scroll out of view.
        if (received.data.interaction) {
          pubSub.publish("scrollToBottom", {
            tag: "sideContentScroll",
          });
        }

        pubSub.publish("onResponseReceived", {
          id: response.id,
          body: response.body,
          context: response.context,
        });
      }
    },
  );

  const handleResponseReceived = useCallback<
    (data: PubSubMap["onResponseReceived"]) => void
  >(
    ({ id, body, context: eventContext }) => {
      log(
        "cyan",
        ["useChatMessages", "onResponseReceived"],
        id,
        agentResponsesMapRef.current,
      );

      setConversation((prevThread) => {
        const newThread = { ...prevThread };

        const mapEntry = agentResponsesMapRef.current[id];

        if (!mapEntry) {
          console.warn(`[useChatMessages] No context found for message ${id}`);
        }

        const context = eventContext ?? mapEntry?.context ?? [];
        const interaction = mapEntry?.interaction ?? false;
        const interactionLabel = mapEntry?.interactionLabel ?? "";
        const verificationState = mapEntry?.verificationState ?? "default";

        if (newThread.entries.find((entry) => entry.id === id)) {
          console.warn(
            `[useChatMessages] Response with ID ${id} already exists`,
          );
          nextFrame(() => {
            config.onDuplicateMessage?.(id, body);
          });
          return prevThread;
        }

        return {
          ...prevThread,
          entries: [
            ...prevThread.entries,
            {
              id: id ?? generateUuid(),
              author: "agent",
              content: body ?? "",
              created: new Date().toISOString(),
              context,
              interaction,
              interactionLabel,
              verificationState,
            },
          ],
        };
      });
    },
    [config],
  );

  // When a finished agent message is send out we add it to the conversation
  useEffect(() => {
    const unsubscribeResponseReceived = pubSub.subscribe(
      "onResponseReceived",
      handleResponseReceived,
    );

    return () => {
      unsubscribeResponseReceived();
    };
  }, [handleResponseReceived, pubSub]);

  // Conversations
  // -------------

  const createNewConversation = (): ChatConversation => ({
    id: generateUuid(),
    thread_id: generateUuid(),
    title: "Temp chat",
    created: new Date().toISOString(),
    entries: [],
  });

  const [conversation, setConversation] = useState<ChatConversation>(
    createNewConversation(),
  );
  const conversationRef = useRef(conversation);

  // Sync the ref with the latest conversation on every render
  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  // User prompts
  // -------------

  useEffect(() => {
    // When a prompt is sent
    const unsubscribePrompt = pubSub.subscribe(
      "onPrompt",
      ({ value, context }) => {
        log("orange", "useChatMessages", "onPrompt:", value);

        setConversation((prevThread) => ({
          ...prevThread,
          entries: [
            ...prevThread.entries,
            {
              id: generateUuid(),
              author: { type: "human" }, // TODO
              content: isString(value) ? value : value.display,
              created: new Date().toISOString(),
              context,
            },
          ],
        }));
      },
    );

    return () => {
      unsubscribePrompt();
    };
  }, []);

  useEffect(() => {
    const unsubscribeFullTranscript = pubSub.subscribe(
      "onFullTranscript",
      ({ value, context }) => {
        log("orange", "useChatMessages", "onFullTranscript:", value);

        setConversation((prevThread) => ({
          ...prevThread,
          entries: [
            ...prevThread.entries,
            {
              id: generateUuid(),
              author: { type: "human" }, // TODO
              content: value,
              created: new Date().toISOString(),
              context,
            },
          ],
        }));
      },
    );

    return () => {
      unsubscribeFullTranscript();
    };
  }, []);

  // Agent response: templates
  // -------------------------

  useWebSocketListener<WebSocketChatTemplatesMessage<TTemplates>>(
    WebSocketChannel.CHAT_TEMPLATES,
    (received) => {
      const tempates = (received.data.data?.templates ?? []) as TTemplates;
      templates.current = tempates;

      log(
        "cyan",
        "useChatMessages",
        "WebSocketChatTemplatesMessage received",
        received,
      );

      // Add templates to conversation
      setConversation((prevConverstation) => {
        if (!templates.current || templates.current.length === 0) {
          console.warn("No templates received", received);
          return prevConverstation;
        }

        const newConversation = { ...prevConverstation };
        newConversation.entries.forEach((entry) => {
          if (
            entry.id === received.data.message_id ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            entry.id === (received.data as any).id // Fallback for old messages
          ) {
            if (!entry.data) {
              entry.data = {};
            }
            entry.data.templates = templates.current;
          }
        });

        return newConversation;
      });
    },
  );

  // Agent response: feedback
  // ------------------------

  useWebSocketListener<WebSocketFeedbackMessage>(
    WebSocketChannel.CHAT_FEEDBACK,
    (message) => {
      if (!message.data.message_id) {
        console.warn("Feedback message without message_id", message);
        return;
      }

      // Add feedback to agent response
      // Not used; only in converstation
      // addOrAppendAgentResponse({
      //   id: message.data.message_id,
      //   feedback: { ...message.data },
      // });

      // Apply feedback to conversation
      setConversation((prevThread) => {
        const newThread = { ...prevThread };

        newThread.entries.forEach((entry) => {
          if (entry.id === message.data.message_id) {
            if (entry.data) {
              entry.data.feedback = message.data;
            } else {
              entry.data = { feedback: message.data };
            }
          }
        });

        return newThread;
      });
    },
  );

  // Clear and ignore chats
  // ----------------------

  const ignoreMessageIDs = useRef<string[]>([]);

  const clearChat = () => {
    ignoreCurrentMessages();
    setAgentResponses({});
    agentResponsesMapRef.current = {};
    completedMessageIDsRef.current.clear();
    setConversation(createNewConversation());
  };

  const ignoreCurrentMessages = () => {
    ignoreMessageIDs.current = [...agentResponseIDs];
  };

  return {
    conversation,
    hasFirstAgentResponse,
    agentResponseIDs,
    agentResponses,
    isStreaming,
    addAgentResponse,
    clearChat,
    ignoreCurrentMessages,
  };
};
